import TrebbleSwitch from "../../../../common/TrebbleSwitch";
import TrebbleSelect from "../../../../common/TrebbleSelect";
import TrebbleInputNumber from "../../../../common/TrebbleInputNumber";
import TrebbleSlider from "../../../../common/TrebbleSlider";
import TrebbleCard from "../../../../common/TrebbleCard";
import TrebbleButton from "../../../../common/TrebbleButton";
import TrebbleMessage from "../../../../common/TrebbleMessage";
import Utils from "../../../../../../js/models/helper/Utils";
import ti18n from 'i18n!nls/ClipCreatori18n';

// Extend Window interface if not already done globally
declare global {
    interface Window {
      getI18n: (i18nData: any, key: string, options?: any) => string;
    }
}

import { PositionSelector } from "./PositionSelector/PositionSelector";
import { SetStateAction, useState, useEffect, useRef } from "react";
import { Template } from "../../VideoEditor";
import { Position } from "../../../remotion/Components/DesignSystem";
import { FieldContainer } from '../CaptionConfig/FieldContainer';
import { MediaSelector } from '../../../../common/MediaPicker/MediaSelector';
import { MediaItem } from '../../../../common/MediaPicker/MediaPicker';
import TrebbleClientAPIHelper from '../../../../../models/helper/TrebbleClientAPI';
import FileUploadHelper from '../../../../../models/helper/FileUploadHelper';

const LOGO_SIZES = Object.freeze({
    SMALL: {
        value: 0.1,
        label: window.getI18n(ti18n, "LOGO_SIZE_SMALL"),
    },
    MEDIUM: {
        value: 0.15,
        label: window.getI18n(ti18n, "LOGO_SIZE_MEDIUM"),
    },
    LARGE: {
        value: 0.2,
        label: window.getI18n(ti18n, "LOGO_SIZE_LARGE"),
    },
    EXTRA_LARGE: {
        value: 0.25,
        label: window.getI18n(ti18n, "LOGO_SIZE_EXTRA_LARGE"),
    },
});

// Demo categories for logos
const LOGO_CATEGORIES = [
    { value: 'brand', label: window.getI18n(ti18n, "LOGO_CATEGORY_BRAND") },
    { value: 'icon', label: window.getI18n(ti18n, "LOGO_CATEGORY_ICON") },
    { value: 'text', label: window.getI18n(ti18n, "LOGO_CATEGORY_TEXT") },
];

const OpacityController = ({ selectedOpacity, setSelectedOpacity }: { selectedOpacity: number, setSelectedOpacity: (opacity: number) => void }) => {
    const onChange = (value: number | null) => {
        const numericValue = Array.isArray(value) ? value[0] : value;
        if (typeof numericValue === 'number' && !isNaN(numericValue)) {
            setSelectedOpacity(numericValue);
        }
    };

    return (
        <div className="flex flex-row">
            <div style={{ flex: "1 1 auto" }}>
                <TrebbleSlider
                    min={1}
                    max={100}
                    onChange={onChange}
                    value={typeof selectedOpacity === "number" ? selectedOpacity : 0}
                />
            </div>
            <div style={{ flex: "0 0 60px" }}>
                <TrebbleInputNumber
                    min={1}
                    max={100}
                    style={{
                        width: "60px",
                        marginLeft: "8px",
                    }}
                    disabled
                    value={selectedOpacity}
                    onChange={onChange}
                    formatter={(value: any) => `${value}%`.replace("%%", "%")}
                    parser={(value: any) => parseInt(value?.replace("%", "") ?? '0', 10)}
                />
            </div>
        </div>
    );
};

export const LogoComponent = ({ selectedTemplate, setSelectedTemplate, workspaceId }: { selectedTemplate: Template, setSelectedTemplate: (template: Template) => void, workspaceId: string | null | undefined }) => {
    const [isPickerVisible, setIsPickerVisible] = useState(false);
    const [logoImages, setLogoImages] = useState<MediaItem[]>([]);
    const [isLoadingLogos, setIsLoadingLogos] = useState(false);
    const [logoError, setLogoError] = useState<string | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [scrollPosition, setScrollPosition] = useState(0);

    const handleMediaChange = (media: MediaItem) => {
        setSelectedTemplate({
            ...selectedTemplate,
            watermarkDesignConfig: {
                ...selectedTemplate.watermarkDesignConfig!,
                imageURL: media.url,
            }
        });
    };

    // Fetch logos from backend
    const fetchLogoImages = async (): Promise<MediaItem[]> => {
        setIsLoadingLogos(true);
        setLogoError(null);
        try {
            const assets = await TrebbleClientAPIHelper.getInstance().getAllLogoImagesForEditor(workspaceId);
            if (!assets) return [];
            const mediaItems = assets.map((asset: any) => ({
                id: asset.assetId,
                url: asset.url,
                type: 'image' as const,
                title: asset.name,
                category: 'brand',
            }));
            setLogoImages(mediaItems);
            return mediaItems;
        } catch (error) {
            console.error('Failed to fetch logos:', error);
            setLogoError('Failed to load logos');
            TrebbleMessage.error('Failed to load logos');
            return [];
        } finally {
            setIsLoadingLogos(false);
        }
    };

    // Upload logo to backend
    const handleLogoUpload = async (file: File): Promise<MediaItem> => {
        try {
            const asset = await FileUploadHelper.getInstance().showUploadDialogAndUploadImageAssetToLibrary(
                file,
                file.name,
                "vector",
                true,
                null,
                { workspaceId }
            );
            return {
                id: asset.assetId,
                url: asset.url,
                type: 'image' as const,
                title: asset.name,
                category: 'brand',
            };
        } catch (error) {
            console.error('Logo upload failed:', error);
            TrebbleMessage.error('Failed to upload logo');
            throw error;
        }
    };

    // Fetch logos when picker is opened
    useEffect(() => {
        if (isPickerVisible && logoImages.length === 0) {
            fetchLogoImages();
        }
    }, [isPickerVisible, logoImages.length, workspaceId]);

    return (
        <div className="flex flex-col h-full">
            <TrebbleMessage._configurator />

            {!isPickerVisible && (
                <div className="sticky top-0 z-20 bg-white">
                    <TrebbleCard size="small"  className="border-b dark:border-neutral-600">
                        <FieldContainer 
                            label="Show logo" 
                            direction="row"
                        >
                            <TrebbleSwitch
                                size="small"
                                checked={selectedTemplate.watermarkDesignConfig?.showWatermark}
                                onChange={(checked: boolean) => setSelectedTemplate({ ...selectedTemplate, watermarkDesignConfig: {  ...selectedTemplate.watermarkDesignConfig!, showWatermark: checked } })}
                            />
                        </FieldContainer>
                    </TrebbleCard>
                </div>
            )}

            <div ref={scrollContainerRef} className="flex-1 overflow-y-auto">
                {selectedTemplate.watermarkDesignConfig?.showWatermark && (
                    <div className="flex flex-col">
                        {!isPickerVisible && (
                            <TrebbleCard size="small" title="Logo Selection" className="mt-4" bodyStyle={{ padding: 0, border: 'none', borderRadius: 0 }}>
                                <FieldContainer label="Choose logo" className="p-2">
                                    {selectedTemplate.watermarkDesignConfig?.imageURL ? (
                                        <>
                                            <div
                                                className="border-2 border-gray-200 bg-white flex items-center justify-center aspect-square w-full h-full p-2 box-border rounded-none mb-4 dark:bg-neutral-800 dark:border-neutral-600"
                                                style={{ borderRadius: 0 }}
                                            >
                                                <img
                                                    src={Utils.getInstance().getImageUrlResizeToSmallSize(selectedTemplate.watermarkDesignConfig.imageURL)}
                                                    alt="Logo"
                                                    style={{
                                                        maxWidth: '100%',
                                                        maxHeight: '100%',
                                                        objectFit: 'contain',
                                                        display: 'block',
                                                    }}
                                                />
                                            </div>
                                            <div className="w-full mt-2 flex">
                                                <TrebbleButton onClick={() => {
                                                    if (scrollContainerRef.current) {
                                                        setScrollPosition(scrollContainerRef.current.scrollTop);
                                                    }
                                                    setIsPickerVisible(true);
                                                }} className="w-full">
                                                    Replace logo...
                                                </TrebbleButton>
                                            </div>
                                        </>
                                    ) : (
                                        <div
                                            onClick={() => {
                                                if (scrollContainerRef.current) {
                                                    setScrollPosition(scrollContainerRef.current.scrollTop);
                                                }
                                                setIsPickerVisible(true);
                                            }}
                                            style={{
                                                width: '100%',
                                                aspectRatio: '1/1',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                border: '2px dashed var(--color-border)',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <TrebbleButton type="primary">Select logo</TrebbleButton>
                                        </div>
                                    )}
                                </FieldContainer>
                            </TrebbleCard>
                        )}

                        {selectedTemplate.watermarkDesignConfig && !isPickerVisible && (
                            <TrebbleCard size="small" title="Logo Properties" className="mt-4">
                                <FieldContainer label="Position">
                                    <PositionSelector 
                                        selectedPosition={selectedTemplate.watermarkDesignConfig.position} 
                                        setSelectedPosition={(newPos) => setSelectedTemplate({ 
                                            ...selectedTemplate, 
                                            watermarkDesignConfig: { 
                                                ...selectedTemplate.watermarkDesignConfig!, 
                                                position: newPos as Position 
                                            } 
                                        })} 
                                    />
                                </FieldContainer>

                                <FieldContainer label="Logo size">
                                    <TrebbleSelect
                                        className="w-full"
                                        options={Object.values(LOGO_SIZES)}
                                        value={selectedTemplate.watermarkDesignConfig.scale}
                                        onChange={(newScale) => setSelectedTemplate({ 
                                            ...selectedTemplate, 
                                            watermarkDesignConfig: { 
                                                ...selectedTemplate.watermarkDesignConfig!, 
                                                scale: newScale as number 
                                            } 
                                        })}
                                    />
                                </FieldContainer>

                                <FieldContainer label="Opacity">
                                    <OpacityController 
                                        selectedOpacity={selectedTemplate.watermarkDesignConfig.opacity} 
                                        setSelectedOpacity={(newOpacity) => setSelectedTemplate({ 
                                            ...selectedTemplate, 
                                            watermarkDesignConfig: { 
                                                ...selectedTemplate.watermarkDesignConfig!, 
                                                opacity: newOpacity 
                                            } 
                                        })} 
                                    />
                                </FieldContainer>
                            </TrebbleCard>
                        )}

                        {isPickerVisible && (
                            <TrebbleCard size="small" title="Logo Library" className="mt-4">
                                <MediaSelector
                                    className="my-2"
                                    compact={true}
                                    selectedMedia={selectedTemplate.watermarkDesignConfig?.imageURL ? {
                                        id: 'current',
                                        url: selectedTemplate.watermarkDesignConfig.imageURL,
                                        type: 'image',
                                        title: 'Current Logo',
                                        category: 'brand'
                                    } : undefined}
                                    onMediaChange={(media) => {
                                        handleMediaChange(media);
                                        setIsPickerVisible(false);
                                        setTimeout(() => {
                                            if (scrollContainerRef.current) {
                                                scrollContainerRef.current.scrollTop = scrollPosition;
                                            }
                                        }, 0);
                                    }}
                                    categories={LOGO_CATEGORIES}
                                    mediaItems={logoImages}
                                    type="image"
                                    uploadToBackend={handleLogoUpload}
                                    isLoading={isLoadingLogos}
                                    error={logoError || undefined}
                                    mediaPerRow={2}
                                    isVisible={isPickerVisible}
                                    onClose={() => {
                                        setIsPickerVisible(false);
                                        setTimeout(() => {
                                            if (scrollContainerRef.current) {
                                                scrollContainerRef.current.scrollTop = scrollPosition;
                                            }
                                        }, 0);
                                    }}
                                />
                            </TrebbleCard>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
